import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ENDPOINTS, resolveAssetUrl } from '../config';

function CountdownTimer({ endDate }) {
    const [timeLeft, setTimeLeft] = useState(getTimeLeft(endDate));

    function getTimeLeft(end) {
        const diff = new Date(end) - new Date();
        if (diff <= 0) return null;
        return {
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((diff / (1000 * 60)) % 60),
            seconds: Math.floor((diff / 1000) % 60),
        };
    }

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(getTimeLeft(endDate));
        }, 1000);
        return () => clearInterval(timer);
    }, [endDate]);

    if (!timeLeft) return <span className="text-sm text-red-500 font-medium">Expired</span>;

    return (
        <div className="flex gap-1.5">
            {timeLeft.days > 0 && (
                <div className="bg-primary text-white px-2 py-1 rounded text-xs font-bold text-center min-w-[36px]">
                    {timeLeft.days}d
                </div>
            )}
            <div className="bg-primary text-white px-2 py-1 rounded text-xs font-bold text-center min-w-[36px]">
                {String(timeLeft.hours).padStart(2, '0')}h
            </div>
            <div className="bg-primary text-white px-2 py-1 rounded text-xs font-bold text-center min-w-[36px]">
                {String(timeLeft.minutes).padStart(2, '0')}m
            </div>
            <div className="bg-primary text-white px-2 py-1 rounded text-xs font-bold text-center min-w-[36px]">
                {String(timeLeft.seconds).padStart(2, '0')}s
            </div>
        </div>
    );
}

function getOfferStatus(offer) {
    const now = new Date();
    const start = new Date(offer.startDate);
    const end = new Date(offer.endDate);
    if (now < start) return 'upcoming';
    if (now > end) return 'expired';
    return 'active';
}

export default function Offers() {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchOffers = async () => {
            try {
                const res = await fetch(ENDPOINTS.OFFERS.PUBLIC);
                const data = await res.json();
                if (data.isOk && data.data) {
                    setOffers(data.data);
                }
            } catch (err) {
                console.error('Error fetching offers:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchOffers();
    }, []);

    return (
        <main className="pt-[10px] pb-12">
            <div className="max-w-[1400px] mx-auto px-4">
                {/* Breadcrumb */}
                <nav className="py-4 text-sm">
                    <ol className="flex items-center gap-2 text-muted-foreground">
                        <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                        <li><i className="ri-arrow-right-s-line"></i></li>
                        <li className="text-foreground font-medium">Offers</li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-foreground mb-2">Current Offers</h1>
                    <p className="text-muted-foreground">Grab the best deals before they expire!</p>
                </div>

                {/* Loading */}
                {loading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="bg-card border border-border rounded-lg overflow-hidden animate-pulse">
                                <div className="w-full h-48 bg-muted"></div>
                                <div className="p-5 space-y-3">
                                    <div className="h-5 bg-muted rounded w-3/4"></div>
                                    <div className="h-4 bg-muted rounded w-1/2"></div>
                                    <div className="h-10 bg-muted rounded w-full"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Empty */}
                {!loading && offers.length === 0 && (
                    <div className="text-center py-16">
                        <i className="ri-price-tag-3-line text-4xl text-muted-foreground/40 mb-4 block"></i>
                        <h3 className="text-xl font-semibold text-foreground mb-2">No active offers</h3>
                        <p className="text-muted-foreground mb-6">Check back soon for exciting deals!</p>
                        <button
                            onClick={() => navigate('/products')}
                            className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all"
                        >
                            Browse Products
                        </button>
                    </div>
                )}

                {/* Offer Cards */}
                {!loading && offers.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {offers.map((offer) => {
                            const status = getOfferStatus(offer);
                            const bannerUrl = offer.bannerImage
                                ? resolveAssetUrl(offer.bannerImage)
                                : null;

                            return (
                                <div
                                    key={offer._id}
                                    onClick={() => navigate(`/offers/${offer.slug}`)}
                                    className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                                >
                                    {/* Banner */}
                                    <div
                                        className="w-full h-48 bg-cover bg-center relative"
                                        style={{
                                            backgroundColor: offer.bgColor || '#F5F5F5',
                                            backgroundImage: bannerUrl ? `url("${bannerUrl}")` : 'none',
                                        }}
                                    >
                                        {!bannerUrl && (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <i className="ri-price-tag-3-line text-5xl text-white/50"></i>
                                            </div>
                                        )}

                                        {/* Discount badge */}
                                        {offer.discountValue > 0 && (
                                            <div className="absolute top-3 right-3 bg-primary text-white px-3 py-1.5 rounded-lg font-bold text-sm shadow-md">
                                                {offer.discountType === 'percentage'
                                                    ? `${offer.discountValue}% OFF`
                                                    : `KSh ${offer.discountValue.toLocaleString()} OFF`
                                                }
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="p-5">
                                        <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                                            {offer.name}
                                        </h3>

                                        {offer.description && (
                                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                                {offer.description}
                                            </p>
                                        )}

                                        {/* Timer / Status */}
                                        <div className="flex items-center justify-between mt-3">
                                            {status === 'active' && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground mb-1">Ends in:</p>
                                                    <CountdownTimer endDate={offer.endDate} />
                                                </div>
                                            )}
                                            {status === 'upcoming' && (
                                                <span className="text-sm text-blue-600 font-medium">
                                                    Starts {new Date(offer.startDate).toLocaleDateString()}
                                                </span>
                                            )}

                                            <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:brightness-110 transition-all ml-auto">
                                                View Offer
                                                <i className="ri-arrow-right-line ml-1"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </main>
    );
}
